import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createPaymentRequest, updatePaymentRequest, submitPaymentRequest, getInvoiceDetail,
  getPaymentRequest, clearPaymentRequest, uploadPaymentRequestDocument,
  deletePaymentRequestDocument,
} from "redux/erp/paymentRequestSlice";
import { PAYMENT_REQUEST_PAYMENT_METHODS } from "app_config";
import { Button, LoadingSpinner, confirm } from 'components/common';
import PageShell, { GradientHeading, GradientLabel } from "./common/PageShell";
import Clickable from "./common/Clickable";
import UnderlineInput, { UNDERLINE_CLASS, UNDERLINE_CELL } from "./common/UnderlineInput";
import SelectDropdown from "./common/SelectDropdown";
import EntryPicker from "./common/EntryPicker";
import { normalizeText } from "./common/dropdownKit";
import UserPicker from "./common/UserPicker";
import { searchManagerUsers, searchAccountants, getAdvances, getFinanceOptions } from "./common/peopleApi";
import AccountingRaisedNotice from "./common/AccountingRaisedNotice";
import AdvanceToSettle, { advanceDefaults, advancePayload } from "./common/AdvanceToSettle";

const FORM_TYPES = [
  { value: "qf01", label: "Payment", iso: "QF-FNC-01", lines: "Payment invoices", description: "Request payment for actual expenses. Select invoices from Doc Search or enter the expense lines." },
  { value: "qf02", label: "Advance Payment", iso: "QF-FNC-02", lines: "Advance amounts", description: "Request an advance before expenses are incurred. Enter the purpose and proposed advance amounts; invoices are not required." },
  { value: "qf03", label: "Clear Downpayment", iso: "QF-FNC-03", lines: "Settlement expenses", description: "Settle an approved advance. Select the advance request and record actual expenses to calculate the remaining payable or receivable." },
];

import InvoiceSearchDrawer from "./common/InvoiceSearchDrawer";
import ErpInvoiceDrawer from "./common/ErpInvoiceDrawer";
import { erpInvoiceToLine } from "./common/erpInvoiceApi";
import DocumentsTable from "./common/DocumentsTable";
import ListHeaderCell from "./common/ListHeaderCell";
import { linkedDocIndex, linkedLineIndex } from "./common/lineLinks";
import useResizableColumns from "./common/useResizableColumns";
import AttachmentsPanel from "./common/AttachmentsPanel";
import InvoicePreviewStack from "./common/InvoicePreviewStack";
import PdfOverlay from "./common/PdfOverlay";
import SplitPane from "./common/SplitPane";
import { invoiceToLine, invoiceAutofill } from "./common/invoiceMapping";
import { formatDate } from "./common/fileMeta";
import { PRIORITY_OPTIONS } from "./common/priority";
import {
  CURRENCIES, requestCurrencyOf, toFormLine, toApiAmounts, hasAmounts, importLine, currencySymbol, formatCurrency, symbolLeads,
} from "./common/lineCurrency";

const EMPTY_ITEM = {
  inv_date: null, inv_no: "", description: "", cost_center: "",
  amount: 0, vat: 0, total: 0, note: "", doc_platform_document_id: "",
};
const toDate = (v) => (v ? new Date(v) : null);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const foldVendor = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().replace(/\s+/g, ' ').trim();

// Phiếu nháp được tạo ngầm khi người lập upload chứng từ trước lúc bấm Save. URL
// vẫn là /new, nên nếu họ F5 thì id nằm trong state React sẽ mất và lần Save sau
// đẻ ra phiếu thứ hai. Ghim id theo tab để lần vào /new kế tiếp quay về đúng phiếu.
const DRAFT_KEY = "pr-draft-in-progress";
const DRAFT_TTL = 2 * 60 * 60 * 1000;

const rememberDraft = (id) => {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ id, at: Date.now() })); } catch { /* storage bị chặn */ }
};
const forgetDraft = () => {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* storage bị chặn */ }
};
const pendingDraftId = () => {
  try {
    const rec = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    if (!rec?.id) return null;
    if (Date.now() - rec.at > DRAFT_TTL) { forgetDraft(); return null; }
    return rec.id;
  } catch { return null; }
};
const TH = "tw-px-2 tw-py-2.5 tw-text-left tw-text-[12px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-geist-fg-2 tw-whitespace-nowrap";
const AMOUNT_TEXTAREA = "tw-w-full tw-h-32 tw-min-h-[128px] tw-box-border tw-resize-y tw-appearance-none tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-px-3 tw-py-3 tw-text-base tw-leading-6 tw-text-geist-fg placeholder:tw-text-geist-fg-3 hover:tw-border-geist-border-2 focus:tw-border-geist-accent focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-geist-accent/20 tw-transition-colors";
// Invoice lines: kéo giãn được, nhớ theo trình duyệt (useResizableColumns).
const LINE_WIDTHS_KEY = "erp.payment-request-form.line-widths.v1";
const LINE_INDEX_WIDTH = 40;
const LINE_ACTION_WIDTH = 48;
const LINE_DEFAULT_WIDTHS = {
  inv_date: 130, inv_no: 150, description: 260, cost: 180, amount: 150, vat: 130, total: 150,
};

// Format tiền cho ô input table (hiển thị có dấu phân nhóm, lưu lại số thuần).
const fmtMoney = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v))) ? "" : Number(v).toLocaleString("en-US");
const parseMoney = (s) => { const c = String(s).replace(/[^\d.]/g, ""); return c === "" ? 0 : Number(c); };

// Spell an integer VND amount into ENGLISH words (Amount in words always matches the total).
const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALES = ["", "thousand", "million", "billion", "trillion"];
const tripleToWords = (n) => {
  const hundreds = Math.floor(n / 100), rest = n % 100;
  let s = "";
  if (hundreds) s += `${ONES[hundreds]} hundred`;
  if (rest) {
    if (s) s += " ";
    if (rest < 20) s += ONES[rest];
    else { s += TENS[Math.floor(rest / 10)]; if (rest % 10) s += `-${ONES[rest % 10]}`; }
  }
  return s;
};
const wholeToWords = (whole) => {
  let n = whole;
  if (n <= 0) return "";
  const groups = [];
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000); }
  const parts = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    parts.push(tripleToWords(groups[i]) + (SCALES[i] ? ` ${SCALES[i]}` : ""));
  }
  return parts.join(" ").trim();
};

// VND: số nguyên "… dong". USD: "… US dollars and NN cents".
const amountToWords = (num, currency = "VND") => {
  const value = Number(num) || 0;
  if (value <= 0) return "";
  const capital = (t) => `${t.charAt(0).toUpperCase()}${t.slice(1)}`;
  if (currency !== "USD") return `${capital(wholeToWords(Math.round(value)))} dong`;

  const cents = Math.round(value * 100);
  const dollars = Math.floor(cents / 100);
  const rest = cents % 100;
  const dollarWords = dollars > 0 ? `${wholeToWords(dollars)} US dollar${dollars === 1 ? "" : "s"}` : "";
  const centWords = rest > 0 ? `${wholeToWords(rest)} cent${rest === 1 ? "" : "s"}` : "";
  return capital([dollarWords, centWords].filter(Boolean).join(" and "));
};

const defaultsFrom = (pr, profile = {}, initialFormType = "qf01") => ({
  payee: pr?.payee || "",
  vendor_account: pr?.vendor_account || "",
  employee_code: pr?.employee_code || profile?.employee?.hr_code || "",
  department: pr?.department || profile?.employee?.department || "",
  payment_method: pr?.payment_method || "bank_transfer",
  bank_name: pr?.bank_name || "",
  bank_account: pr?.bank_account || "",
  request_date: toDate(pr?.request_date) || new Date(),
  due_date: toDate(pr?.due_date),
  amount_in_words: pr?.amount_in_words || "",
  note: pr?.note || "",
  manager: pr?.manager_user_id
    ? { id: pr.manager_user_id, email: pr.manager_email, name: pr.manager_name }
    : null,
  // Blank = the Settings accountant, taken when the request is submitted.
  accountant: pr?.accountant_review?.id
    ? { id: pr.accountant_review.id, email: pr.accountant_review.email, name: pr.accountant_review.name }
    : null,
  form_type: pr?.form_type || initialFormType || "qf01",
  priority: pr?.priority || "medium",
  workflow_type: pr?.workflow_type || "standard",
  purchase_order_type: pr?.purchase_order_type || pr?.workflow_type || 'standard',
  payment_summary: pr?.payment_summary || pr?.note || '',
  parent_advance: pr?.parent_advance || (pr?.parent_advance_id ? { id: pr.parent_advance_id } : null),
  ...advanceDefaults(pr, requestCurrencyOf(pr?.items || [])),
  // Không còn bước chọn hóa đơn riêng -> form luôn mở sẵn 1 dòng trống để nhập tay.
  // Một currency cho cả phiếu (chọn ở đầu bảng dòng) — suy từ dòng đã lưu.
  currency: requestCurrencyOf(pr?.items || []),
  items: pr?.items?.length
    ? pr.items.map((i) => ({ ...toFormLine(i), inv_date: toDate(i.inv_date) }))
    : [{ ...EMPTY_ITEM }],
  // Bảng tài liệu: hóa đơn doc-platform + file tự upload, chung một mảng.
  documents_records: (pr?.documents || []).map((d) => ({ ...d })),
});

// Bootstrap icon as a component (for TwButton leftIcon/rightIcon). Ignores `size` (font icon).
const biIcon = (name) => function Bi() { return <i className={`bi ${name}`} />; };

// Khối section có tiêu đề — gom field cho dễ quét mắt (Geist: card + header nhẹ).
const FormSection = ({ icon, title, right, children, bodyClassName = "tw-p-5" }) => (
  <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-flex-shrink-0 tw-overflow-hidden">
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-5 tw-py-2.5 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
        {icon && <i className={`bi ${icon} tw-text-geist-fg-3`} />}
        <h3 className="tw-text-base tw-font-semibold tw-text-geist-fg tw-m-0 tw-truncate">{title}</h3>
      </div>
      {right}
    </div>
    <div className={bodyClassName}>{children}</div>
  </div>
);

FormSection.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node,
  right: PropTypes.node,
  children: PropTypes.node,
  bodyClassName: PropTypes.string,
};

const PaymentRequestFormInner = ({ paymentRequest, onCancel, onSaved, initialFormType = "qf01" }) => {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile_user?.data || {});
  const isEdit = Boolean(paymentRequest?.id);
  // Id thật của phiếu. Khác `paymentRequest?.id` ở chỗ: trong luồng tạo mới, phiếu
  // có thể đã được lưu nháp ngầm (người dùng bấm Upload trước khi Save) -> từ lúc
  // đó mọi lần lưu là update chứ không tạo thêm phiếu.
  const [recordId, setRecordId] = useState(paymentRequest?.id || null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [erpOpen, setErpOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);        // documentId đang xem (để highlight)
  const [previewUrl, setPreviewUrl] = useState(null);  // URL PDF inline (s3 view_url)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);       // overlay PDF toàn màn hình (mobile)
  const urlCache = useRef({});                         // docId -> view_url (tránh fetch lại)
  const detailCache = useRef({});                      // docId -> invoice detail đầy đủ (dùng lại cho add)
  const inflight = useRef({});                         // docId -> promise đang chờ (pane + bảng hỏi cùng lúc)
  const stackRef = useRef(null);                       // pane preview: scrollTo(docId)
  const [candidate, setCandidate] = useState(null);    // hóa đơn đang xem thử từ drawer, chưa thêm

  const [saveMode, setSaveMode] = useState(null);
  const submittingRef = useRef(false);
  const { register, control, handleSubmit, setValue, getValues, reset, watch, formState: { errors, isSubmitting, isDirty } } =
    useForm({ defaultValues: defaultsFrom(paymentRequest, profile, initialFormType) });
  const submitOnce = (mode, onValid) => async (event) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaveMode(mode);
    try { await handleSubmit(onValid)(event); } finally { submittingRef.current = false; }
  };

  // Form giờ là 1 trang riêng nên đóng nhầm = mất hết nội dung đang nhập.
  // Chặn cả 2 đường ra: nút Cancel/Back trong app, và đóng/reload tab.
  // The app's own dialog, like every other confirmation (closing the tab still gets the
  // browser's — browsers allow nothing else in beforeunload).
  const confirmLeave = useCallback(async () => {
    if (!isDirty || isSubmitting) return true;
    const autoDrafted = !isEdit && !!recordId;
    return confirm({
      title: "Leave without saving?",
      message: autoDrafted
        ? "You have unsaved changes. This request is already saved as a draft — the latest edits will be lost."
        : "You have unsaved changes. They will be lost.",
      confirmText: "Leave without saving",
      cancelText: "Stay",
    });
  }, [isDirty, isSubmitting, isEdit, recordId]);

  const handleCancel = useCallback(async () => {
    if (!(await confirmLeave())) return;
    forgetDraft();
    onCancel?.();
  }, [confirmLeave, onCancel]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);
  // Mảng hóa đơn/dòng chi phí: dùng useFieldArray để RHF đăng ký field thật (đảm bảo
  // luôn nằm trong payload submit). Render cấu trúc bảng/list từ `fields` (key = field.id).
  // keyName: "_key" để KHÔNG ghi đè `id` thật của item (id DB cần giữ cho update).
  const { fields, append, remove } = useFieldArray({ control, name: "items", keyName: "_key" });
  const itemKeys = useRef([]);
  itemKeys.current = fields.map((field) => field._key);
  const persistedItemIds = useRef((paymentRequest?.items || []).map((item) => item.id).filter(Boolean));
  const {
    fields: docFields, append: appendDoc, remove: removeDoc, replace: replaceDocs,
  } = useFieldArray({ control, name: "documents_records", keyName: "_key" });

  const showBank = watch("payment_method") === "bank_transfer";
  const currency = watch("currency") || "VND";
  const requestDate = watch("request_date");
  const watchedItems = watch("items") || [];
  const watchedDocs = watch("documents_records") || [];
  // Một mảng documents_records, hai chỗ hiển thị: hóa đơn Doc Search ở bảng trên,
  // file tự tải lên ở Attachments dưới Note. `_idx` giữ vị trí thật trong mảng để
  // register(`documents_records.${_idx}.note`) và removeDoc(_idx) trỏ đúng phần tử.
  const allDocs = docFields.map((f, i) => ({ ...f, ...(watchedDocs[i] || {}), _idx: i }));
  const invoiceDocs = allDocs.filter((d) => d.source !== "upload");
  const attachmentDocs = allDocs.filter((d) => d.source === "upload");
  // docId đã thêm -> InvoiceSearchPanel hiện "Added"/disable (không thêm trùng).
  const addedIds = new Set(
    watchedDocs.map((d) => d.document_id || d.doc_platform_document_id).filter(Boolean)
  );
  const formType = watch("form_type") || "qf01";
  const selectedForm = FORM_TYPES.find((type) => type.value === formType) || FORM_TYPES[0];
  const hasInvoices = formType !== "qf02";

  const bankName = watch("bank_name");
  const payee = watch('payee');
  const entryField = (name, kind, options = {}) => <Controller control={control} name={name}
    rules={options.required ? { required: true } : undefined}
    render={({ field, fieldState }) => <EntryPicker {...field} kind={kind} paymentRequestId={recordId}
      cell={options.cell} aria-label={options.label || name} aria-required={!!options.required} placeholder={options.placeholder}
      context={kind === 'bank_account' ? { payee, bank_name: bankName } : {}}
      className={fieldState.error ? 'tw-border-geist-error' : ''}
      onChange={(value) => {
        // A new payee means another account and bank. Bank name and bank account are
        // independent: correcting the bank keeps the account number typed.
        if (name === 'payee' && normalizeText(value).trim() !== normalizeText(field.value).trim()) {
          setValue('bank_account', '', { shouldDirty: true });
          setValue('bank_name', '', { shouldDirty: true });
          setValue('vendor_account', '', { shouldDirty: true });
        }
        field.onChange(value);
      }}
      onSelect={['bank_account', 'payee'].includes(kind) ? (entry) => {
        const c = entry.context || {};
        setValue('vendor_account', c.vendor_account || '', { shouldDirty: true });
        if (kind === 'bank_account') {
          if (c.payee) setValue('payee', c.payee, { shouldDirty: true, shouldValidate: true });
          if (c.bank_name) setValue('bank_name', c.bank_name, { shouldDirty: true });
        }
      } : undefined} />} />;
  const [advances, setAdvances] = useState([]);
  const isPoRecord = watch('purchase_order_type') === 'po_record';
  // Raised by Accounting (server decides by who raises it): no Manager / accountant in
  // charge — the Chief Accountant (or Head of Finance) approves first.
  const [accounting, setAccounting] = useState({ raised: !!paymentRequest?.raised_by_accounting, chief: false });
  useEffect(() => {
    let alive = true;
    getFinanceOptions()
      .then((r) => alive && setAccounting({ raised: !!r?.data?.raised_by_accounting, chief: !!r?.data?.raised_by_chief_accountant }))
      .catch(() => {}); // keeps the request's own flag: the form still shows the pickers when unsure
    return () => { alive = false; };
  }, []);
  const raisedByAccounting = accounting.raised && !isPoRecord;

  // QF-03: nạp danh sách phiếu tạm ứng (QF-02) đã duyệt để chọn quyết toán.
  useEffect(() => {
    if (formType === "qf03") {
      getAdvances().then((r) => setAdvances(r?.data || [])).catch(() => setAdvances([]));
    }
  }, [formType]);

  // Thêm/bớt dòng qua useFieldArray (append/remove) — RHF giữ field đã đăng ký nên dòng
  // luôn vào payload submit (trước đây setValue mảng chưa đăng ký -> mất dòng khi lưu).
  const addItem = (item) => append(item);
  const { widthOf, startResize, nudge, resetWidth } = useResizableColumns(LINE_WIDTHS_KEY, LINE_DEFAULT_WIDTHS);
  const lineResize = { start: startResize, nudge, reset: resetWidth };
  const lineColumns = [
    ...(hasInvoices ? [{ key: "inv_date", label: "Inv date" }, { key: "inv_no", label: "Inv No" }] : []),
    { key: "description", label: "Description" },
    { key: "cost", label: "Cost" },
    { key: "amount", label: "Amount", align: "right" },
    ...(hasInvoices ? [{ key: "vat", label: "VAT", align: "right" }] : []),
    { key: "total", label: "Total", align: "right" },
  ];
  const linesMinWidth = lineColumns.reduce((sum, c) => sum + widthOf(c.key), LINE_INDEX_WIDTH + LINE_ACTION_WIDTH);
  const num = (v) => Number(v) || 0;
  const lineTotal = (line) => num(line.amount) + num(line.vat);
  const totals = watchedItems.reduce(
    (s, i) => ({ amount: s.amount + num(i.amount), vat: s.vat + num(i.vat), total: s.total + lineTotal(i) }),
    { amount: 0, vat: 0, total: 0 }
  );

  // Đổi currency khi đã nhập số: số giữ nguyên nhưng đổi đơn vị (38,000,000 VND →
  // 38,000,000 USD) — hỏi lại trước, không đổi ngầm.
  const changeCurrency = async (next) => {
    if (next === currency) return;
    if (hasAmounts(getValues("items") || [])) {
      const ok = await confirm({
        title: `Switch this request to ${next}?`,
        message: `The amounts already entered stay the same numbers but become ${next}. Check every line after switching.`,
        confirmText: `Switch to ${next}`,
        cancelText: "Keep " + currency,
      });
      if (!ok) return;
    }
    setValue("currency", next, { shouldDirty: true });
  };

  // Dòng dựng từ hoá đơn → một ô Amount; hoá đơn khác currency thì đổi currency của
  // phiếu (nếu chưa có số) hoặc từ chối.
  const lineFromInvoice = (rawLine) => {
    const res = importLine(rawLine, currency, getValues("items") || []);
    if (res.error) { toast.error(res.error); return null; }
    if (res.currency !== currency) {
      setValue("currency", res.currency, { shouldDirty: true });
      toast.info(`Request currency set to ${res.currency} from the invoice.`);
    }
    return res.line;
  };

  // Ô tiền trong table: hiển thị có dấu phân nhóm, lưu lại số thuần.
  // Ký hiệu tiền đứng cạnh ô ($ trước, ₫ sau) như mọi số tiền khác trên phiếu; giá trị
  // nhập vẫn là số trần.
  const moneyCell = (name) => {
    const symbol = <span aria-hidden="true" className="tw-text-sm tw-text-primary tw-shrink-0">{currencySymbol(currency)}</span>;
    return (
      <Controller control={control} name={name}
        render={({ field }) => (
          <div className="tw-flex tw-items-baseline tw-gap-1">
            {symbolLeads(currency) && symbol}
            <UnderlineInput cell inputMode="decimal" className="tw-text-right !tw-text-primary tw-tabular-nums"
              aria-label={`Line ${Number(name.split(".")[1]) + 1} ${name.endsWith("vat") ? "VAT" : "Amount"} (${currency})`} value={fmtMoney(field.value)}
              onChange={(e) => field.onChange(parseMoney(e.target.value))} />
            {!symbolLeads(currency) && symbol}
          </div>
        )} />
    );
  };

  // Pane preview: mọi hóa đơn doc-platform đã thêm, xếp dọc theo thứ tự bảng
  // Documents; hóa đơn đang xem thử từ drawer (chưa thêm) nằm cuối.
  const previewItems = watchedDocs
    .map((d) => ({ ...d, id: d.document_id || d.doc_platform_document_id }))
    .filter((d) => d.id)
    .map((d) => ({
      id: d.id,
      label: d.inv_no || d.vendor_name || "Invoice",
      meta: [formatDate(d.inv_date), d.vendor_name].filter(Boolean).join(" · "),
      amount: d.amount,
    }));
  if (candidate && !addedIds.has(candidate.id)) previewItems.push({ ...candidate, pending: true });

  useEffect(() => {
    {
      reset(defaultsFrom(paymentRequest, profile, initialFormType));
      persistedItemIds.current = (paymentRequest?.items || []).map((item) => item.id).filter(Boolean);
      setRecordId(paymentRequest?.id || null);
      setPreview(null); setPreviewUrl(null); setPreviewLoading(false); setPdfOpen(false); setCandidate(null);
      urlCache.current = {}; detailCache.current = {}; inflight.current = {};
    }
    // Reset only when switching to another request; a refreshed paymentRequest/profile
    // object for the same id must not wipe what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, paymentRequest?.id]);

  // Lấy detail hóa đơn (parse OCR + view_url) 1 lần, cache theo docId — dùng chung cho
  // cả preview và add (preview trước -> add không phải fetch lại).
  const fetchDetail = useCallback(async (documentId) => {
    if (!documentId) return null;
    if (detailCache.current[documentId]) return detailCache.current[documentId];
    if (!inflight.current[documentId]) {
      inflight.current[documentId] = dispatch(getInvoiceDetail(documentId)).unwrap()
        .then((inv) => {
          if (inv) {
            const docId = inv.document_id || documentId;
            detailCache.current[docId] = inv;
            const url = inv.view_url || inv.download_url || null;
            if (url) urlCache.current[docId] = url;
          }
          return inv;
        })
        .finally(() => { delete inflight.current[documentId]; });
    }
    return inflight.current[documentId];
  }, [dispatch]);

  // Pane preview tự hỏi URL khi hóa đơn sắp lọt vào tầm nhìn.
  const resolvePreviewUrl = useCallback(async (documentId) => {
    await fetchDetail(documentId);
    return urlCache.current[documentId] || null;
  }, [fetchDetail]);

  // Xem PDF: dùng view_url (presigned inline) — cache theo docId, warm luôn detail.
  const showPreview = useCallback(async (documentId) => {
    if (!documentId) { setPreview(null); setPreviewUrl(null); return; }
    setPreview(documentId);
    stackRef.current?.scrollTo(documentId);
    if (urlCache.current[documentId]) { setPreviewUrl(urlCache.current[documentId]); return; }
    setPreviewUrl(null); setPreviewLoading(true);
    try {
      await fetchDetail(documentId);
      setPreviewUrl(urlCache.current[documentId] || null);
    } catch { setPreviewUrl(null); }
    finally { setPreviewLoading(false); }
  }, [fetchDetail]);

  // Người dùng cuộn pane / chọn trong menu: chỉ đổi dòng đang chọn, không cuộn lại.
  // previewUrl theo luôn để nút "View PDF" (mobile) mở đúng hóa đơn.
  const handleStackActive = useCallback((documentId) => {
    setPreview(documentId);
    setPreviewUrl(urlCache.current[documentId] || null);
  }, []);

  // Mở form (kể cả khi edit): nạp sẵn PDF của tài liệu đầu tiên để đối chiếu.
  useEffect(() => {
    if (preview) return;
    const first = (getValues("documents_records") || [])[0];
    const docId = first?.document_id || first?.doc_platform_document_id;
    if (docId) showPreview(docId);
    // Auto-open the first PDF once per request; depending on `preview` would re-open it
    // whenever the user closes the preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, showPreview, paymentRequest?.id]);

  // Amount in words tự đọc theo TỔNG cộng dồn (khớp Amount in number). User vẫn sửa được;
  // chỉ tự cập nhật khi tổng thay đổi (thêm/bớt hóa đơn hoặc sửa số tiền).
  useEffect(() => {
    if (totals.total > 0) setValue("amount_in_words", amountToWords(totals.total, currency), { shouldDirty: true });
  }, [setValue, totals.total, currency]);

  const buildPayload = (data) => ({
    payee: data.payee, vendor_account: data.vendor_account || '', employee_code: data.employee_code, department: data.department,
    payment_method: data.payment_method, bank_name: data.bank_name, bank_account: data.bank_account,
    // request_date: server đặt lúc tạo, không gửi lên.
    due_date: data.due_date || null,
    amount_in_words: data.amount_in_words, note: data.note,
    // Raised by Accounting: no Manager and no accountant in charge.
    manager_user_id: raisedByAccounting ? null : data.manager?.id || null,
    // PO record has no Accountant step (Accounting raises it).
    ...(data.purchase_order_type === 'po_record' ? {} : { accountant_review_user_id: raisedByAccounting ? null : data.accountant?.id || null }),
    form_type: data.form_type || "qf01",
    purchase_order_type: data.form_type === 'qf02' ? 'standard' : data.purchase_order_type || 'standard',
    payment_summary: data.payment_summary,
    priority: data.priority || "medium",
    // HSBC trade loan không còn ở form (2026-10-25): Kế toán tick ở Payment - APP khi đối
    // ứng là HSBC* (PaymentDetailsFields). Không gửi field này để không ghi đè lựa chọn đó.
    ...advancePayload(data),
    items_attributes: [...(data.items || []).map((i, idx) => ({
      id: i.id, inv_date: i.inv_date, inv_no: i.inv_no, description: i.description,
      cost_center: i.cost_center, ...toApiAmounts(i.amount, data.currency),
      vat: Number(i.vat) || 0, total: lineTotal(i), note: i.note,
      doc_platform_document_id: i.doc_platform_document_id, position: idx,
    })), ...persistedItemIds.current
      .filter((id) => !(data.items || []).some((item) => String(item.id) === String(id)))
      .map((id) => ({ id, _destroy: true }))],
    // Chỉ gửi dòng doc-platform: bản ghi file upload do endpoint upload_document
    // tạo ở server, FE không dựng lại (gửi lên sẽ thiếu blob -> validate hỏng).
    documents_records_attributes: (data.documents_records || [])
      .map((d, idx) => ({ ...d, position: idx }))   // position theo thứ tự hiển thị của cả bảng
      .filter((d) => d.source === "doc_search" || d.source === "erp_invoice")
      .map((d) => ({
        id: d.id,
        source: d.source,
        doc_platform_document_id: d.document_id || d.doc_platform_document_id,
        inv_no: d.inv_no, inv_date: d.inv_date, vendor_name: d.vendor_name,
        amount: d.amount, note: d.note, position: d.position,
      })),
  });

  // partial = lưu nháp ngầm (chưa điền đủ form) -> server bỏ qua validate đầy đủ.
  const persist = async (data, { partial = false } = {}) => {
    const savedKeys = [...itemKeys.current];
    const payload = { ...buildPayload(data), ...(partial ? { partial: true } : {}) };
    // Retain database IDs after every save, including the hidden draft created
    // for an upload. Otherwise the next save inserts the same invoice again.
    // Match React field keys so edits/removals made while saving stay intact.
    const syncSavedIds = (attrs) => {
      if (attrs?.items) {
        persistedItemIds.current = attrs.items.map((item) => item.id).filter(Boolean);
        attrs.items.forEach((item, idx) => {
          const currentIdx = itemKeys.current.indexOf(savedKeys[idx]);
          if (currentIdx >= 0 && item.id) setValue(`items.${currentIdx}.id`, item.id);
        });
      }
      (attrs?.documents || []).filter((doc) => doc.source === 'doc_search').forEach((doc) => {
        const docId = doc.document_id || doc.doc_platform_document_id;
        const idx = (getValues('documents_records') || []).findIndex((current) =>
          current.source === 'doc_search' && (current.document_id || current.doc_platform_document_id) === docId);
        if (idx >= 0) setValue(`documents_records.${idx}.id`, doc.id);
      });
    };
    if (recordId) {
      const res = await dispatch(updatePaymentRequest({ id: recordId, data: payload })).unwrap();
      syncSavedIds(res?.data?.attributes);
      return { id: recordId, attrs: res?.data?.attributes };
    }
    const res = await dispatch(createPaymentRequest(payload)).unwrap();
    const id = res?.data?.attributes?.id || res?.data?.id;
    if (id) {
      setRecordId(id);
      if (!isEdit) rememberDraft(id);
    }
    syncSavedIds(res?.data?.attributes);
    return { id, attrs: res?.data?.attributes };
  };

  // Đồng bộ bảng tài liệu theo dữ liệu server trả về (nguồn sự thật sau khi lưu).
  const syncDocuments = (attrs) => {
    if (!attrs?.documents) return;
    replaceDocs(attrs.documents.map((d) => ({ ...d })));
  };

  // Upload cần id, và endpoint upload trả về toàn bộ bảng tài liệu của server —
  // nên phải lưu nháp TRƯỚC mỗi lần upload, nếu không các dòng doc-search chưa lưu
  // sẽ bị syncDocuments ghi đè mất.
  const saveBeforeUpload = async () => {
    const isFirstSave = !recordId;
    const { id } = await persist(getValues(), { partial: true });
    if (!id) throw new Error("Could not save the draft before uploading.");
    if (isFirstSave) toast.info("Draft saved so the file can be attached.");
    return id;
  };

  const handleUploadFiles = async (files) => {
    setUploading(true);
    try {
      const id = await saveBeforeUpload();
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("documents[]", f));
      const res = await dispatch(uploadPaymentRequestDocument({ id, formData: fd })).unwrap();
      syncDocuments(res?.data?.attributes);
      toast.success(files.length > 1 ? `${files.length} files attached.` : "File attached.");
    } catch (e) {
      toast.error(e?.error || e?.message || "Upload failed.");
    } finally { setUploading(false); }
  };

  // Gỡ tài liệu. Dòng đã lưu -> gọi API (xóa cả blob); dòng chưa lưu -> bỏ tại chỗ.
  // Hóa đơn (Doc Search / From PO ERP) gỡ kèm dòng chi phí sinh từ nó — `lineIdx`
  // truyền vào khi người dùng xoá từ phía dòng (removeItem).
  const handleRemoveDocument = async (idx, doc, lineIdx = linkedLineIndex(getValues("items"), doc)) => {
    const docId = doc.document_id || doc.doc_platform_document_id;
    try {
      if (doc.id && recordId) {
        await dispatch(deletePaymentRequestDocument({ id: recordId, documentId: doc.id })).unwrap();
      }
      removeDoc(idx);
      if (lineIdx >= 0) remove(lineIdx);
      if (docId && preview === docId) { setPreview(null); setPreviewUrl(null); }
      toast.success(lineIdx >= 0 ? `Removed invoice ${doc.inv_no || ""} and its line.` : "Document removed.");
    } catch (e) { toast.error(e?.error || e?.message || "Failed to remove document."); }
  };

  // Xoá một dòng chi phí sinh từ hoá đơn thì gỡ luôn hoá đơn ở bảng Invoices phía
  // trên (và ngược lại) — giữ một bên sẽ lệch: hoá đơn ERP còn trong phiếu thì phiếu
  // vẫn bị khoá journal APP và hoá đơn đó không lập được phiếu khác.
  const removeItem = (idx) => {
    const docs = getValues("documents_records") || [];
    const docIdx = linkedDocIndex(docs, getValues(`items.${idx}`));
    if (docIdx < 0) { remove(idx); return; }
    handleRemoveDocument(docIdx, docs[docIdx], idx);
  };

  // Bấm 1 dòng trong bảng tài liệu: hóa đơn doc-platform -> preview trong pane phải;
  // file tự upload -> mở tab mới (ActiveStorage trả link trực tiếp).
  const handlePreviewDocument = (doc) => {
    if (doc.source === "erp_invoice") return; // posted in AX — no file to show
    const docId = doc.document_id || doc.doc_platform_document_id;
    if (docId) { showPreview(docId); return; }
    if (doc.url) window.open(doc.url, "_blank", "noopener");
  };

  // Validate các trường KHÔNG bắt được bằng rule field-level: bank (khi chuyển khoản)
  // và "≥1 dòng có số tiền > 0". Set inline error + toast. Áp cho cả Save lẫn Submit.
  const checkExtra = (data) => {
    let ok = true;
    const hasAmount = (data.items || []).some((i) => lineTotal(i) > 0);
    if (!hasAmount) { toast.warning("Add at least one line with an amount."); ok = false; }
    // Nhắc chứ không chặn: QF-01 là thanh toán theo hóa đơn nên gần như luôn có
    // chứng từ, nhưng vẫn có ca hợp lệ chưa kịp đính kèm lúc lưu nháp.
    if (ok && data.form_type === "qf01" && (data.documents_records || []).length === 0) {
      toast.warning("No document attached — add the invoice or upload the supporting file.");
    }
    return ok;
  };

  const onSave = async (data) => {
    if (!checkExtra(data)) return;
    try { const { id } = await persist(data); forgetDraft(); toast.success("Payment request saved."); onSaved?.(id); }
    catch (e) { toast.error(e?.error || e?.message || "Save failed."); }
  };
  const onSaveAndSubmit = async (data) => {
    if (!raisedByAccounting && !data.manager?.id) { toast.warning("Please select a Manager (approver) before submitting."); return; }
    if (!checkExtra(data)) return;
    try {
      const { id } = await persist(data);
      await dispatch(submitPaymentRequest(id)).unwrap();
      forgetDraft();
      toast.success("Payment request submitted for approval.");
      onSaved?.(id);
    } catch (e) { toast.error(e?.error || e?.message || "Submit failed."); }
  };

  // Preview 1 hóa đơn từ kết quả search (chưa thêm dòng) — nối tạm vào cuối pane.
  const handlePreviewInvoice = (documentId, item) => {
    if (!addedIds.has(documentId)) {
      setCandidate({
        id: documentId,
        label: item?.inv_no || item?.vendor_name || "Invoice",
        meta: [formatDate(item?.inv_date), item?.vendor_name].filter(Boolean).join(" · "),
        amount: item?.amount,
      });
    }
    showPreview(documentId);
  };

  // Đóng drawer: bỏ hóa đơn xem thử mà không thêm khỏi pane.
  const closeSearch = () => { setSearchOpen(false); setCandidate(null); };

  // Thêm 1 hóa đơn thành 1 dòng. Dùng lại detail đã cache (preview trước đó) nếu có.
  // Trả true khi hóa đơn đã nằm trong phiếu (vừa thêm hoặc có sẵn) — modal View dựa
  // vào đó để tự đóng.
  const handleAddInvoice = async (documentId) => {
    try {
      const inv = await fetchDetail(documentId);
      if (!inv) return false;
      const selectedVendor = getValues('payee')?.trim();
      if (selectedVendor && inv.vendor_name && foldVendor(selectedVendor) !== foldVendor(inv.vendor_name)) {
        toast.error(`Invoice ${inv.inv_no || ''} belongs to ${inv.vendor_name}. All invoices must belong to ${selectedVendor}.`);
        return false;
      }
      const docId = inv.document_id || documentId;

      // Đã thêm rồi -> chỉ preview lại, không thêm trùng.
      const currentDocs = getValues("documents_records") || [];
      if (currentDocs.some((d) => (d.document_id || d.doc_platform_document_id) === docId)) {
        showPreview(docId);
        toast.info("Invoice already added.");
        return true;
      }

      const current = getValues("items") || [];
      const line = lineFromInvoice(invoiceToLine(inv, EMPTY_ITEM));
      if (!line) return false;
      // Form mở sẵn 1 dòng trống -> hóa đơn đầu tiên điền vào đó thay vì thêm dòng mới.
      const blankIdx = current.findIndex(
        (i) => !i.doc_platform_document_id && !i.inv_no && !lineTotal(i)
      );
      if (blankIdx >= 0) {
        Object.entries(line).forEach(([k, v]) => setValue(`items.${blankIdx}.${k}`, v, { shouldDirty: true }));
      } else {
        addItem(line);
      }

      appendDoc({
        source: "doc_search",
        document_id: docId,
        inv_no: inv.inv_no || "",
        inv_date: inv.inv_date || null,
        vendor_name: inv.vendor_name || "",
        amount: line.total || inv.total_amount || inv.amount || 0,
        note: "",
      });
      setCandidate((c) => (c?.id === docId ? null : c));

      const wasEmpty = currentDocs.length === 0;

      // The first invoice can establish the recipient; later invoices must match.
      if (wasEmpty) {
        const fill = invoiceAutofill(inv);
        if (!selectedVendor && inv.vendor_name) fill.payee = inv.vendor_name;
        if (getValues('bank_account')) delete fill.bank_account;
        if (getValues('bank_name')) delete fill.bank_name;
        Object.entries(fill).forEach(([k, v]) => setValue(k, v, { shouldDirty: true }));
      }

      showPreview(docId);
      toast.success(`Added ${inv.inv_no || "invoice"}.`);
      return true;
    } catch (e) {
      toast.error(e?.error || e?.message || "Failed to load invoice.");
      return false;
    }
  };

  // "From ERP": one posted AX invoice → one line. Same vendor rule as Doc Search;
  // an invoice number already on the request is not added twice (one line per invoice).
  const handleAddErpInvoice = (inv) => {
    const selectedVendor = getValues('payee')?.trim();
    if (selectedVendor && inv.vendor_name && foldVendor(selectedVendor) !== foldVendor(inv.vendor_name)) {
      toast.error(`Invoice ${inv.inv_no} belongs to ${inv.vendor_name}. All invoices must belong to ${selectedVendor}.`);
      return false;
    }
    const current = getValues("items") || [];
    const docs = getValues("documents_records") || [];
    if (docs.some((d) => (d.document_id || d.doc_platform_document_id) === inv.document_id)
      || current.some((i) => String(i.inv_no || '').trim() === inv.inv_no)) {
      toast.info(`Invoice ${inv.inv_no} is already on this request.`);
      return true;
    }
    const line = lineFromInvoice(erpInvoiceToLine(inv, EMPTY_ITEM));
    if (!line) return false;
    const blankIdx = current.findIndex((i) => !i.doc_platform_document_id && !i.inv_no && !lineTotal(i));
    if (blankIdx >= 0) {
      Object.entries(line).forEach(([k, v]) => setValue(`items.${blankIdx}.${k}`, v, { shouldDirty: true }));
    } else {
      addItem(line);
    }
    // Kept as an invoice document: one live request per invoice, and the request is
    // paid with journal APP only (the invoice journal is already posted in AX).
    appendDoc({
      source: "erp_invoice", document_id: inv.document_id, inv_no: inv.inv_no, inv_date: inv.inv_date,
      vendor_name: inv.vendor_name, amount: line.total, note: "",
    });
    if (!selectedVendor && inv.vendor_name) setValue('payee', inv.vendor_name, { shouldDirty: true });
    toast.success(`Added ${inv.inv_no} from ERP.`);
    return true;
  };
  const erpAddedKeys = new Set(watchedDocs.filter((d) => d.source === "erp_invoice").map((d) => d.document_id || d.doc_platform_document_id));

  // Toolbar: back + tiêu đề + chọn form type + các nút hành động, gom hết lên
  // header cho gọn (trước đây form type chiếm một card riêng và các nút nằm ở
  // footer cố định). `tw-flex-wrap` để ở màn hẹp hàng nút tự xuống dòng.
  const header = (
    <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-3 tw-flex-wrap">
      <Clickable onClick={handleCancel} title="Back"
        className="tw-flex-shrink-0 tw-p-1.5 tw-rounded-geist-sm tw-text-geist-fg-3 hover:tw-text-geist-fg hover:tw-bg-geist-100 tw-transition-colors">
        <i className="bi bi-arrow-left" />
      </Clickable>
      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
        <i className="bi bi-receipt tw-text-geist-fg-3 tw-flex-shrink-0" />
        <GradientHeading className="tw-text-lg tw-truncate">
          {isEdit ? `Edit ${selectedForm.label} · ${paymentRequest?.code || selectedForm.iso}` : `New ${selectedForm.label}`}
        </GradientHeading>
      </div>

      {/* Keep the type fixed once a draft has been persisted. */}
      <div className="tw-w-full sm:tw-w-[310px] tw-flex-shrink-0">
        <SelectDropdown
          value={formType}
          aria-label="Request type"
          disabled={Boolean(recordId)}
          options={FORM_TYPES.map((ft) => ({ value: ft.value, label: `${ft.label} · ${ft.iso}` }))}
          onChange={(v) => {
            setValue("form_type", v, { shouldDirty: true });
            if (v !== "qf03") setValue("parent_advance", null);
            setSearchOpen(false);
          }}
        />
      </div>

      <div className="tw-flex tw-gap-2 tw-ml-auto tw-flex-shrink-0">
        <Button variant="secondary" size="md" disabled={isSubmitting} onClick={handleCancel}>Cancel</Button>
        {/* saveMode says which button runs; submitOnce drops a second click before the re-render. */}
        <Button variant="soft" size="md" disabled={isSubmitting || uploading} loading={isSubmitting && saveMode === "draft"}
          onClick={submitOnce("draft", onSave)}>
          {isSubmitting && saveMode === "draft" ? "Saving…" : "Save draft"}
        </Button>
        <Button variant="primary" size="md" leftIcon={biIcon("bi-send")} disabled={isSubmitting || uploading} loading={isSubmitting && saveMode === "submit"}
          onClick={submitOnce("submit", onSaveAndSubmit)}>
          {isSubmitting && saveMode === "submit" ? "Submitting…" : <>Save &amp; submit</>}
        </Button>
      </div>
    </div>
  );

  return (
    <PageShell header={header}>
      <div className="tw-flex tw-flex-col tw-gap-4 tw-h-full tw-px-6 tw-py-5">
        {(() => {
          // left: form (scroll riêng)
          const formColumn = (
          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-4 tw-pr-1">
          <FormSection icon="bi-receipt" title={`${selectedForm.label} · ${selectedForm.iso}`}>
            <p className="tw-m-0 tw-text-sm tw-text-geist-fg-2">{selectedForm.description}</p>
          </FormSection>
          {/* Hóa đơn Doc Search — đặt trên cùng vì đây là bước đầu tiên người lập
              làm. File tự tải lên nằm ở Attachments dưới ô Note. */}
          {hasInvoices && <DocumentsTable
            documents={invoiceDocs}
            activeId={preview}
            onPreview={handlePreviewDocument}
            onRemove={handleRemoveDocument}
            onSearch={() => setSearchOpen(true)}
            onErpSearch={() => setErpOpen(true)}
            register={register}
          />}

          {/* Payee & approver */}
          <FormSection icon="bi-person-badge" title="Payee & approver">
            {/* Two fields per row (2026-10-23): the explanations moved behind an (i) next to the label. */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
              <div className="tw-min-w-0">
                <GradientLabel required>Payment summary</GradientLabel>
                <UnderlineInput aria-label="Payment summary" {...register('payment_summary', { required: true, maxLength: 2000 })}
                  placeholder="Briefly describe what this payment is for…" />
                {errors.payment_summary && <p className="tw-m-0 tw-mt-1 tw-text-geist-error tw-text-xs">Enter the payment summary.</p>}
              </div>
              <div className="tw-min-w-0">
                <GradientLabel required hint="One request pays one vendor. Add one line for each invoice from this vendor.">
                  Vendor name / payee
                </GradientLabel>
                {entryField('payee', 'payee', { required: true, label: 'Vendor name / payee', placeholder: 'Search or enter vendor name…' })}
              </div>
              {raisedByAccounting && <AccountingRaisedNotice chief={accounting.chief} className="md:tw-col-span-2" />}
              {!raisedByAccounting && <div className="tw-min-w-0">
                <GradientLabel required>Manager (first approver)</GradientLabel>
                <Controller control={control} name="manager" rules={{ validate: (v) => !!v?.id }}
                  render={({ field, fieldState }) => (
                    <div className={fieldState.error ? "tw-rounded-geist-sm tw-ring-1 tw-ring-geist-error" : ""}>
                      <UserPicker aria-label="Line manager" value={field.value} onChange={field.onChange} fetcher={searchManagerUsers} placeholder="Select a manager in your department…" />
                    </div>
                  )} />
              </div>}
              {!isPoRecord && !raisedByAccounting && (
                <div className="tw-min-w-0">
                  <GradientLabel required
                    hint="Responsible for the Accountant step: reviews the request and prepares the journals. Others in the accounting team can help.">
                    Accountant in charge
                  </GradientLabel>
                  {/* Required: decides who is responsible at the Accountant step. The list is the
                      accounting team (Settings → Signers → Accountant); the team can still help. */}
                  <Controller control={control} name="accountant" rules={{ validate: (v) => !!v?.id }}
                    render={({ field, fieldState }) => (
                      <div className={fieldState.error ? "tw-rounded-geist-sm tw-ring-1 tw-ring-geist-error" : ""}>
                        <UserPicker aria-label="Accountant in charge" value={field.value} onChange={field.onChange} fetcher={searchAccountants}
                          placeholder="Select the accountant in charge…" />
                      </div>
                    )} />
                  {errors.accountant && <p className="tw-m-0 tw-mt-1 tw-text-geist-error tw-text-xs">Choose the accountant in charge.</p>}
                </div>
              )}
              {formType === "qf03" && (
                <AdvanceToSettle control={control} setValue={setValue} advances={advances} currency={currency} totalCost={totals.amount} />
              )}
            </div>
          </FormSection>

          {/* Payment details */}
          <FormSection icon="bi-wallet2" title="Payment details">
            {/* 2 hàng × 4 cột: ai / khi nào (Employee · Department · Request date · Due date),
                rồi trả thế nào (Priority · Payment method · Bank account · Bank name). */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-4">
              <div className="tw-min-w-0"><GradientLabel required>Employee code</GradientLabel>{entryField('employee_code', 'employee_code', { required: true, label: 'Employee code' })}</div>
              <div className="tw-min-w-0"><GradientLabel required>Department</GradientLabel>{entryField('department', 'department', { required: true, label: 'Department' })}</div>
              {/* Request date = ngày lập phiếu, server tự đặt (không sửa được). */}
              <div className="tw-min-w-0">
                <GradientLabel hint="Set automatically to the day the request is raised.">Request date</GradientLabel>
                <UnderlineInput readOnly aria-label="Request date" title="Set to the day the request is raised"
                  value={requestDate ? formatDate(requestDate) : ""} className="tw-text-geist-fg-2" />
              </div>
              {/* Due date = ngày người lập mong được thanh toán; HSBC chi đúng ngày này. */}
              <div className="tw-min-w-0">
                <GradientLabel hint="The day you want it paid — HSBC pays on this day. Blank = as soon as the payment is sent.">Due date</GradientLabel>
                <Controller control={control} name="due_date"
                  rules={{ validate: (d) => !d || !requestDate || startOfDay(d) >= startOfDay(requestDate) || "Due date cannot be before the request date." }}
                  render={({ field, fieldState }) => (
                    <>
                      <DatePicker className={`${UNDERLINE_CLASS} ${fieldState.error ? "tw-border-geist-error" : ""}`}
                        dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" isClearable
                        minDate={requestDate || new Date()} selected={field.value}
                        onChange={(d) => field.onChange(d ? adjustToNoon(d) : null)}
                        customInput={<input autoComplete="off" aria-label="Due date" />} />
                      {fieldState.error && (
                        <p role="alert" className="tw-m-0 tw-mt-1 tw-text-[11px] tw-text-geist-error">{fieldState.error.message}</p>
                      )}
                    </>
                  )} />
              </div>
              <div className="tw-min-w-0">
                <GradientLabel>Priority</GradientLabel>
                <Controller control={control} name="priority"
                  render={({ field }) => <SelectDropdown value={field.value} options={PRIORITY_OPTIONS} onChange={field.onChange} />} />
              </div>
              <div className="tw-min-w-0">
                <GradientLabel required>Payment method</GradientLabel>
                <Controller control={control} name="payment_method" rules={{ required: true }}
                  render={({ field }) => <SelectDropdown value={field.value} options={PAYMENT_REQUEST_PAYMENT_METHODS} onChange={field.onChange} />} />
              </div>
              {showBank && <div className="tw-min-w-0"><GradientLabel>Bank account</GradientLabel>{entryField('bank_account', 'bank_account', { label: 'Bank account', placeholder: 'Search account number / bank…' })}</div>}
              {showBank && <div className="tw-min-w-0"><GradientLabel>Bank name</GradientLabel>{entryField('bank_name', 'bank_name', { label: 'Bank name' })}</div>}
            </div>
          </FormSection>

          {/* Invoice lines */}
          {/* flex-shrink-0: card có overflow-hidden -> mất min-height:auto -> bị flex co về 0 (ẩn bảng). Khóa lại. */}
          <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden tw-flex-shrink-0">
            <div className="tw-px-5 tw-py-3 tw-border-b tw-border-solid tw-border-geist-border tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                <GradientLabel className="tw-mb-0 tw-text-sm">{selectedForm.lines}</GradientLabel>
                {/* Currency của cả phiếu — mọi dòng dùng chung (một lệnh chi = một loại tiền). */}
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span id="pr-currency-label" className="tw-text-[12px] tw-font-medium tw-text-geist-fg-3">Currency</span>
                  <div role="radiogroup" aria-labelledby="pr-currency-label"
                    className="tw-inline-flex tw-p-0.5 tw-bg-geist-100 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm">
                    {CURRENCIES.map((c) => (
                      <Clickable key={c} role="radio" aria-checked={currency === c} onClick={() => changeCurrency(c)}
                        className={`tw-px-3 tw-py-1 tw-text-[12px] tw-font-semibold tw-rounded-[4px] tw-transition ${
                          currency === c ? "tw-bg-geist-bg tw-text-geist-fg tw-shadow-geist-card" : "tw-text-geist-fg-3 hover:tw-text-geist-fg"
                        }`}>
                        {c}
                      </Clickable>
                    ))}
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-4">
                {preview && (
                  <Clickable onClick={() => setPdfOpen(true)} className="lg:tw-hidden tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-geist-accent"><i className="bi bi-file-earmark-pdf" /> View PDF</Clickable>
                )}
                {hasInvoices && (
                  <Clickable onClick={() => setSearchOpen(true)}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
                    <i className="bi bi-search" /> From invoice
                  </Clickable>
                )}
                {hasInvoices && (
                  <Clickable onClick={() => setErpOpen(true)} title="Vendor invoices already posted in AX — paid with journal APP"
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
                    <i className="bi bi-database" /> From PO ERP
                  </Clickable>
                )}

                <Clickable onClick={() => addItem({ ...EMPTY_ITEM })}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
                  <i className="bi bi-plus-lg" /> Add line
                </Clickable>
              </div>
            </div>
            <div className="tw-overflow-x-auto tw-px-3">
              {/* table-fixed + colgroup: độ rộng theo cột kéo giãn; hẹp hơn tổng thì cuộn ngang. */}
              <table className="tw-w-full tw-border-collapse tw-table-fixed" style={{ minWidth: linesMinWidth }}>
                <colgroup>
                  <col style={{ width: LINE_INDEX_WIDTH }} />
                  {lineColumns.map((c) => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                  <col style={{ width: LINE_ACTION_WIDTH }} />
                </colgroup>
                <thead>
                  <tr className="tw-border-b tw-border-solid tw-border-geist-border">
                    <th className={`${TH} tw-text-center`}>#</th>
                    {lineColumns.map((c) => (
                      <ListHeaderCell key={c.key} column={c} resize={lineResize}
                        className={`${TH} tw-relative ${c.align === "right" ? "tw-text-right" : ""}`} />
                    ))}
                    <th className={TH}></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.length === 0 && (
                    <tr>
                      <td colSpan={hasInvoices ? 9 : 6} className="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-geist-fg-3">
                        No cost line yet — use “Add line”{hasInvoices ? ", “From invoice” or “From PO ERP”" : ""}.
                      </td>
                    </tr>
                  )}
                  {fields.map((it, idx) => (
                    <tr key={it._key} className={idx % 2 === 1 ? "tw-bg-geist-bg-2" : "tw-bg-white"}>
                      <td className="tw-px-2 tw-py-2.5 tw-h-11 tw-text-center tw-text-sm tw-font-bold tw-text-geist-fg-3">{idx + 1}</td>
                      {hasInvoices && <><td className="tw-px-2 tw-py-2">
                        <Controller control={control} name={`items.${idx}.inv_date`}
                          render={({ field: f }) => <DatePicker className={UNDERLINE_CELL} autoComplete="off" dateFormat="dd/MM/yyyy" selected={f.value} onChange={d => f.onChange(adjustToNoon(d))} />} />
                      </td>
                      <td className="tw-px-2 tw-py-2">{entryField(`items.${idx}.inv_no`, 'invoice', { cell: true, label: `Invoice ${idx + 1}` })}</td></>}
                      <td className="tw-px-2 tw-py-2">{entryField(`items.${idx}.description`, 'description', { cell: true, label: `Description ${idx + 1}` })}</td>
                      <td className="tw-px-2 tw-py-2">{entryField(`items.${idx}.cost_center`, 'dim_cost_center', { cell: true, label: `Cost center ${idx + 1}` })}</td>
                      <td className="tw-px-2 tw-py-2">{moneyCell(`items.${idx}.amount`)}</td>
                      {hasInvoices && <td className="tw-px-2 tw-py-2">{moneyCell(`items.${idx}.vat`)}</td>}
                      <td className="tw-px-2 tw-py-2"><UnderlineInput cell readOnly className="!tw-text-primary tw-font-semibold tw-text-right tw-tabular-nums" aria-label={`Line ${idx + 1} Total`} value={formatCurrency(lineTotal(watchedItems[idx] || {}), currency)} /></td>
                      <td className="tw-px-2 tw-py-2 tw-text-center">
                        <Clickable onClick={() => removeItem(idx)} title="Remove"
                          className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-geist-sm tw-text-geist-fg-3 hover:tw-bg-geist-100 hover:tw-text-geist-error"><i className="bi bi-trash tw-text-[14px]" /></Clickable>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-solid tw-border-geist-border">
                    <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-2" colSpan={hasInvoices ? 5 : 3}>Total</td>
                    <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-base tw-font-semibold tw-text-primary tw-tabular-nums tw-whitespace-nowrap">{formatCurrency(totals.amount, currency)}</td>
                    {hasInvoices && <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-base tw-font-semibold tw-text-primary tw-tabular-nums tw-whitespace-nowrap">{formatCurrency(totals.vat, currency)}</td>}
                    <td className="tw-px-2 tw-py-2.5 tw-text-right tw-text-base tw-font-semibold tw-text-primary tw-tabular-nums tw-whitespace-nowrap">{formatCurrency(totals.total, currency)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Amounts, note & attachments */}
          <FormSection icon="bi-cash-coin" title="Amounts, note & attachments">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
              <div className="sm:tw-col-span-2">
                <label htmlFor="payment-amount-number" className="tw-block tw-text-sm tw-font-medium tw-text-geist-fg-2 tw-mb-2">Amount in number</label>
                <UnderlineInput id="payment-amount-number" readOnly value={formatCurrency(totals.total, currency)}
                  className="!tw-h-12 !tw-text-2xl !tw-text-primary tw-font-semibold tw-tabular-nums !tw-bg-primary-50" />
              </div>
              <div className="tw-min-w-0">
                <label htmlFor="payment-amount-words" className="tw-block tw-text-sm tw-font-medium tw-text-geist-fg-2 tw-mb-2">Amount in words</label>
                <textarea id="payment-amount-words" rows={4} {...register("amount_in_words")} placeholder="e.g. One hundred million dong"
                  className={AMOUNT_TEXTAREA} />
              </div>
              <div className="tw-min-w-0">
                <label htmlFor="payment-note" className="tw-block tw-text-sm tw-font-medium tw-text-geist-fg-2 tw-mb-2">Note</label>
                <textarea id="payment-note" rows={4} {...register("note")} className={AMOUNT_TEXTAREA} />
              </div>
              {/* Tệp đính kèm ngay dưới Note, như email. Chỉ PDF và ảnh. */}
              <div className="sm:tw-col-span-2">
                <AttachmentsPanel
                  attachments={attachmentDocs}
                  uploading={uploading || isSubmitting}
                  onUpload={handleUploadFiles}
                  onRemove={handleRemoveDocument}
                />
              </div>
            </div>
          </FormSection>
          </div>
          );
          if (!hasInvoices) return <div className="tw-flex tw-flex-1 tw-min-h-0 tw-animate-fade-in-up">{formColumn}</div>;

          // right: PDF preview để đối chiếu (ẩn trên mobile -> dùng nút "View PDF").
          // SplitPane: kéo để chia tỉ lệ form | hoá đơn, nút ‹/› ẩn hẳn khung xem.
          return (
            <div className="tw-flex tw-flex-1 tw-min-h-0 tw-animate-fade-in-up">
              <SplitPane storageKey="erp.payment-request-form.split.v1" defaultLeft={60} rightLabel="invoice preview"
                left={formColumn}
                right={(
                  <div className="tw-flex-1 tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden tw-flex tw-flex-col tw-min-h-0">
                    <InvoicePreviewStack
                      ref={stackRef}
                      items={previewItems}
                      activeId={preview}
                      onActiveChange={handleStackActive}
                      resolveUrl={resolvePreviewUrl}
                    />
                  </div>
                )} />
            </div>
          );
        })()}
      </div>

      {hasInvoices && <InvoiceSearchDrawer
        paymentRequestId={recordId}
        show={searchOpen}
        onClose={closeSearch}
        onPreview={handlePreviewInvoice}
        onAdd={handleAddInvoice}
        resolveDetail={fetchDetail}
        addedIds={addedIds}
        activeId={preview}
      />}

      {hasInvoices && <ErpInvoiceDrawer
        show={erpOpen}
        onClose={() => setErpOpen(false)}
        onAdd={handleAddErpInvoice}
        addedKeys={erpAddedKeys}
        paymentRequestId={recordId}
      />}

      <PdfOverlay show={pdfOpen} url={previewUrl} loading={previewLoading} onClose={() => setPdfOpen(false)} />
    </PageShell>
  );
};

PaymentRequestFormInner.propTypes = {
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    raised_by_accounting: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
  }),
  onCancel: PropTypes.func,
  onSaved: PropTypes.func,
  initialFormType: PropTypes.string,
};

/**
 * Route wrapper — /payment-requests/new (?type=qf02) và /payment-requests/:id/edit.
 *
 * Trước đây form nằm trong SlideDrawer mở từ danh sách: không deep-link được,
 * F5 là mất sạch nội dung đang nhập, và trang detail phải tự mở lại drawer.
 * Tách thành route thì link chia sẻ được và Back của trình duyệt chạy đúng.
 */
const PaymentRequestForm = ({ mode = "create" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { recordData: pr, loading } = useSelector((s) => s.erp_payment_request);

  const isEdit = mode === "edit";
  // Vào /new sau khi F5 giữa chừng: phiếu nháp đã được tạo ngầm rồi, mở lại nó
  // thay vì để lần Save sau tạo thêm một phiếu nữa.
  const [resumeId] = useState(() => (mode === "edit" ? null : pendingDraftId()));

  useEffect(() => {
    if (isEdit || !resumeId) return;
    forgetDraft();
    toast.info("Continuing the draft you started earlier.");
    navigate(`/payment-requests/${resumeId}/edit`, { replace: true });
  }, [isEdit, resumeId, navigate]);

  useEffect(() => {
    if (isEdit && id) dispatch(getPaymentRequest(id));
    return () => { dispatch(clearPaymentRequest()); };
  }, [dispatch, isEdit, id]);

  const goBack = useCallback(() => {
    if (isEdit && id) navigate(`/payment-requests/${id}`);
    else navigate("/payment-requests");
  }, [navigate, isEdit, id]);

  const onSaved = useCallback((savedId) => {
    if (savedId) navigate(`/payment-requests/${savedId}`);
    else navigate("/payment-requests");
  }, [navigate]);

  if (resumeId && !isEdit) {
    return <PageShell header={null}><LoadingSpinner message="Loading your draft..." /></PageShell>;
  }

  if (isEdit && (loading || String(pr?.id || "") !== String(id))) {
    return <PageShell header={null}><LoadingSpinner message="Loading..." /></PageShell>;
  }

  return (
    <PaymentRequestFormInner
      // Remount khi đổi phiếu — form state của react-hook-form không tự reset
      // hết khi chỉ đổi prop.
      key={isEdit ? `edit-${id}` : `new-${searchParams.get("type") || "qf01"}`}
      paymentRequest={isEdit ? pr : null}
      initialFormType={searchParams.get("type") || "qf01"}
      onCancel={goBack}
      onSaved={onSaved}
    />
  );
};

PaymentRequestForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]),
};

export default PaymentRequestForm;
export { PaymentRequestFormInner };
